import { Component, EventEmitter, OnInit, Output } from '@angular/core'
import {
  AccountInfo,
  RootState,
  selectFirstFocusedAccountInfo,
  selectSingleFocusedSecurity,
  selectSubSleeveAccountId,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { DisplayDateFormat, ISODateFormat, formatDate } from '@northfork/basics'
import { Daterange } from '@northfork/ng-basics'
import { combineLatest, Subject } from 'rxjs'
import { distinctUntilChanged, takeUntil, tap } from 'rxjs/operators'
import { SecurityService } from '../services/security.service'
import { SleeveSimpleAccounts } from '../models/sleeve-simple-accounts.model'
import { FormControl } from '@angular/forms'
import { PricingService } from '../services/pricing.service'

export enum PriceSourceSelectionOption {
  HIERARCHY = 'Hierarchy',
  AVAILABLE = 'Available',
}

@Component({
  selector: 'hs-pricing-data-menu',
  templateUrl: './pricing-data-menu.component.html',
})
export class PricingDataMenuComponent implements OnInit {
  @Output() beginDateEmitter = new EventEmitter<string>()
  @Output() endDateEmitter = new EventEmitter<string>()
  @Output() sourceOptionEmitter = new EventEmitter<PriceSourceSelectionOption>()
  @Output() accountIdWithSecurityId = new EventEmitter<number>()
  securityDetails = ''
  securityDescription = ''
  destroySubSleeveAccountIdSubject = new Subject<void>()
  destroySingleFocusedSecuritySubject = new Subject<void>()
  destroyFirstFocusedAccountSubject = new Subject<void>()
  isSleeveAggregate = false
  accountId = ''
  selectedAccount = new FormControl()
  subAccounts: SleeveSimpleAccounts[] = []
  secIds: number[] = []
  isLoadingSubSleeveAccounts = false

  constructor(
    private readonly store: Store<RootState>,
    private readonly securityService: SecurityService,
    private readonly pricingService: PricingService,
  ) {}

  priorReconDate = this.getPriorReconDate()
  parsedEndDate = this.parseDate(this.priorReconDate)

  monthToDate: Daterange = {
    title: 'Month to Date',
    beginDate: this.parseDate(
      new Date(
        this.priorReconDate.getFullYear(),
        this.priorReconDate.getMonth(),
        1,
      ),
    ),
    endDate: this.parsedEndDate,
  }

  priorMonthToDate: Daterange = {
    title: 'Prior Month to Date',
    beginDate: this.parseDate(this.getPreviousMonth()),
    endDate: this.parsedEndDate,
  }

  yearToDate: Daterange = {
    title: 'Year to Date',
    beginDate: this.parseDate(
      new Date(this.priorReconDate.getFullYear(), 0, 1),
    ),
    endDate: this.parsedEndDate,
  }

  selectOptions = [
    PriceSourceSelectionOption.HIERARCHY,
    PriceSourceSelectionOption.AVAILABLE,
  ]
  selection = PriceSourceSelectionOption.HIERARCHY

  customDateTemplates: Daterange[] = [
    this.monthToDate,
    this.priorMonthToDate,
    this.yearToDate,
  ]

  daterange: Daterange = {
    beginDate: this.parseDate(this.getEndDate()),
    endDate: this.parsedEndDate,
  }

  readonly options = {
    min: this.parseDate(this.getMinYearToDispaly()),
    max: this.parsedEndDate,
    disabled: false,
    beginDateRequired: true,
    endDateRequired: true,
  }

  dateFormat = DisplayDateFormat
  onSelection = (sourceSelectionOption: PriceSourceSelectionOption) => {
    this.sourceOptionEmitter.emit(sourceSelectionOption)
  }

  onDateApply = (dateRange: Daterange) => {
    if (dateRange.beginDate) this.beginDateEmitter.emit(dateRange.beginDate)
    if (dateRange.endDate) this.endDateEmitter.emit(dateRange.endDate)
  }

  ngOnInit() {
    this.selectedAccount.valueChanges.subscribe(() => {
      this.onAccountChange(this.selectedAccount.value)
    })
    this.store
      .select(selectSubSleeveAccountId)
      .pipe(
        takeUntil(this.destroySubSleeveAccountIdSubject),
        tap(accountId => {
          this.selectedAccount.setValue(accountId)
        }),
      )
      .subscribe()

    if (this.daterange.beginDate)
      this.beginDateEmitter.emit(this.daterange.beginDate)

    if (this.daterange.endDate) this.endDateEmitter.emit(this.daterange.endDate)

    this.store
      .select(selectFirstFocusedAccountInfo)
      .pipe(
        takeUntil(this.destroyFirstFocusedAccountSubject),
        distinctUntilChanged(),
        tap(selectedAccount => {
          if (selectedAccount) {
            this.setSessionAccountInfo(selectedAccount)
          }
        }),
      )
      .subscribe()

    combineLatest([
      this.store
        .select(selectSingleFocusedSecurity)
        .pipe(distinctUntilChanged()),
      this.store
        .select(selectFirstFocusedAccountInfo)
        .pipe(distinctUntilChanged()),
    ])
      .pipe(takeUntil(this.destroySingleFocusedSecuritySubject))
      .subscribe(([securityId, accountInfo]) => {
        if (securityId && accountInfo) {
          this.setSecurityDetails(accountInfo.accountId, securityId)
        }
      })
  }

  setSessionAccountInfo(sessionAccount: AccountInfo) {
    this.securityDetails = ''
    this.securityDescription = ''
    if ('sleeveAggregate' == sessionAccount.accountType) {
      this.isLoadingSubSleeveAccounts = true
      this.isSleeveAggregate = true
      this.pricingService
        .fetchSubAccounts(sessionAccount.accountId)
        .subscribe(accounts => {
          const accountsWithDisplayLabel = accounts.map(account => ({
            ...account,
            displayLabel: account.id + ' ' + account.name,
          }))
          this.subAccounts = accountsWithDisplayLabel
          this.isLoadingSubSleeveAccounts = false
        })
    } else {
      this.isSleeveAggregate = false
      this.accountId = sessionAccount.accountId.toString()
    }
  }

  onAccountChange = (accountId: number) => {
    this.accountIdWithSecurityId.emit(accountId)
  }

  setSecurityDetails(accountId: number, securityId: number) {
    this.securityService
      .getSecurityDetails(accountId, securityId)
      .subscribe(securityDetails => {
        this.securityDetails = securityDetails.identifier
        this.securityDescription = securityDetails.description
      })
  }

  ngOnDestroy(): void {
    this.destroySubSleeveAccountIdSubject.next()
    this.destroySingleFocusedSecuritySubject.next()
    this.destroyFirstFocusedAccountSubject.next()
    this.destroySubSleeveAccountIdSubject.complete()
    this.destroySingleFocusedSecuritySubject.complete()
    this.destroyFirstFocusedAccountSubject.complete()
  }

  getPriorReconDate() {
    const date = new Date()
    let diff = 1
    const day = date.getDay()
    if (day == 0 || day == 1) diff = day + 2
    date.setDate(date.getDate() - diff)
    return date
  }
  getPreviousMonth() {
    const date = new Date(this.priorReconDate)
    date.setDate(1)
    date.setMonth(date.getMonth() - 1)
    return date
  }
  getMinYearToDispaly() {
    const date = new Date(this.priorReconDate)
    date.setDate(1)
    date.setFullYear(date.getFullYear() - 2)
    return date
  }
  parseDate(date: Date) {
    const val = formatDate(date, ISODateFormat)
    if (val) return val
    return ''
  }
  getEndDate() {
    const date = new Date(this.priorReconDate)
    date.setDate(date.getDate() - 30)
    return date
  }
}
